import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Badge } from '@/shared/components/ui/badge';
import type { Priority } from '@/shared/lib/priority';

const PRIORITY_COLOR: Record<Priority, string> = {
  urgent: 'text-chart-5',
  high: 'text-chart-3',
  medium: 'text-chart-4',
  low: 'text-chart-2',
};

interface TemplatePriorityBadgeProps {
  /** `undefined` renders as "No priority" — the backend's `"None"` round-trips this way. */
  priority: Priority | undefined;
}

/**
 * No shared/reusable priority badge exists in the codebase (`tasks/components/TaskListView/PriorityPicker.tsx`
 * only exports an interactive picker with a non-exported icon) — built locally, see
 * `tasks/current-story.md` Design Phase Output.
 */
export function TemplatePriorityBadge({ priority }: TemplatePriorityBadgeProps) {
  const { t } = useTranslation('tasks');

  if (!priority) {
    return (
      <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
        {t('priority.none')}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="gap-1 font-normal">
      <Flag className={cn('size-3', PRIORITY_COLOR[priority])} aria-hidden />
      {t(`priority.${priority}`)}
    </Badge>
  );
}
